import { parsePong, rttMs, stddev, deriveQuality } from '../bff';
import type { BffHealth, PongReadout, WsStatus } from '../useBffPing';

// =====================================================================
// In-band link-health ledger (plans/single-websocket/plan.md).
//
// The same sampling useBffPing runs on its dedicated socket, restated as a pure
// class fed by the COCKPIT socket's own ping/pong stream — so the connection
// readout can describe the socket that actually carries calls. Semantics mirror
// useBffPing exactly at the same interval: a ping unanswered for 3×interval is a
// miss; pongs quiet for 2.5×interval mark the link stale; jitter is the stddev
// of the last 20 RTTs. Framework-free with an injectable clock, so the whole
// state machine is unit-tested without a socket or React.
//
// Lifetime: one ledger per cockpit mount, surviving reconnects (like
// useBffPing's state does) — reconnectCount only means something across sockets.
// =====================================================================

const RTT_WINDOW = 20; // RTTs kept for the jitter (stddev) calc — as useBffPing

// A gap between 1Hz sweeps longer than this means the browser suspended/throttled
// the tab's timers (background tab, lid closed). Misses observed across such a gap
// say nothing about the link — the PINGS never went out on schedule — so the sweep
// forgives them rather than counting a burst of phantom misses on resume (Codex P1
// on #668). Staleness still shows until the next real pong (~one interval).
const SUSPENSION_GAP_MS = 5_000;

export class LinkHealthLedger {
  private status: WsStatus = 'connecting';
  // Outstanding pings (n -> sentAt) for miss detection.
  private readonly pending = new Map<number, number>();
  private readonly rttWindow: number[] = [];
  private lastPongAt: number | null = null;
  private lastSweepAt: number | null = null;
  private lastPong: PongReadout | null = null;
  private pongCount = 0;
  private rtt: number | null = null;
  private jitterMs = 0;
  private missCount = 0;
  private reconnectCount = 0;

  constructor(private readonly intervalMs: number) {}

  connecting(): void {
    this.status = 'connecting';
  }

  // `isReconnect` = a previous socket had already opened this mount.
  // Deliberate deviation from useBffPing: the stale clock starts at socket-OPEN,
  // not at the first pong. A socket that opens and wedges before ever answering a
  // ping would otherwise read `good` with rtt null forever — the exact
  // wedged-socket shape this ledger exists to expose (Codex P1 on #668).
  opened(isReconnect: boolean, now: number = Date.now()): void {
    this.status = 'open';
    this.lastPongAt = now;
    if (isReconnect) this.reconnectCount += 1;
  }

  closed(): void {
    this.status = 'closed';
  }

  pingSent(n: number, now: number = Date.now()): void {
    this.pending.set(n, now);
  }

  // Feed a raw WS message; returns true when it was a pong (and was recorded),
  // false for anything else — the caller passes non-pongs on to its frame parser.
  pongReceived(raw: unknown, now: number = Date.now()): boolean {
    const pong = parsePong(raw);
    if (!pong) return false;
    const r = rttMs(pong, now);
    if (typeof pong.n === 'number') this.pending.delete(pong.n);
    if (r !== null) {
      this.rttWindow.push(r);
      if (this.rttWindow.length > RTT_WINDOW) this.rttWindow.shift();
      this.jitterMs = stddev(this.rttWindow);
      this.rtt = r;
    }
    this.lastPongAt = now;
    this.lastPong = { frame: pong, rtt: r };
    this.pongCount += 1;
    return true;
  }

  // The 1Hz sweep: age out pings whose pong never came (useBffPing's tick).
  sweep(now: number = Date.now()): void {
    const gap = this.lastSweepAt === null ? 0 : now - this.lastSweepAt;
    this.lastSweepAt = now;
    if (gap > SUSPENSION_GAP_MS) {
      // Timers were suspended: re-date the outstanding pings so each gets a fresh
      // full miss window from resume instead of being counted the moment we wake.
      // A genuinely dead link still surfaces — re-dated pings miss one window
      // later, and staleness clears only on a real pong.
      for (const n of this.pending.keys()) this.pending.set(n, now);
      return;
    }
    const missTimeoutMs = this.intervalMs * 3;
    for (const [n, sentAt] of this.pending) {
      if (now - sentAt > missTimeoutMs) {
        this.pending.delete(n);
        this.missCount += 1;
      }
    }
  }

  // Snapshot in the exact BffHealth shape the control box already consumes.
  // `online` is the browser's navigator.onLine picture, owned by the caller.
  read(online: boolean, now: number = Date.now()): BffHealth {
    const lastPongAgeMs = this.lastPongAt === null ? null : now - this.lastPongAt;
    const stale = lastPongAgeMs !== null && lastPongAgeMs > this.intervalMs * 2.5;
    return {
      status: this.status,
      lastPong: this.lastPong,
      pongCount: this.pongCount,
      rtt: this.rtt,
      jitterMs: this.jitterMs,
      missCount: this.missCount,
      reconnectCount: this.reconnectCount,
      lastPongAgeMs,
      online,
      quality: deriveQuality({
        open: this.status === 'open',
        online,
        rttMs: this.rtt,
        jitterMs: this.jitterMs,
        stale,
      }),
    };
  }
}
